import React, { useState } from 'react';
import { X, Bell } from 'lucide-react';
import { getTheme } from '../App';

export interface AlertDraft {
  id: string;
  symbol: string;
  condition: 'price_gt' | 'price_lt' | 'funding_gt' | 'funding_lt'
           | 'bid_oracle_abs' | 'bid_oracle_pct' | 'ask_oracle_abs' | 'ask_oracle_pct';
  threshold: number;
  note: string;
  one_shot: boolean;
  active: boolean;
  created_at: number;
  triggered_at: number | null;
}

interface Props {
  dark?: boolean;
  symbols: string[];
  onClose: () => void;
  onSubmit: (alert: AlertDraft) => void;
}

const CONDITIONS = [
  { group: 'Fiyat',   value: 'price_gt',       label: 'Fiyat >',             hint: 'mark price (USD)' },
  { group: 'Fiyat',   value: 'price_lt',        label: 'Fiyat <',             hint: 'mark price (USD)' },
  { group: 'Funding', value: 'funding_gt',      label: 'Funding Rate >',      hint: '% (örn: 0.01)' },
  { group: 'Funding', value: 'funding_lt',      label: 'Funding Rate <',      hint: '% (örn: -0.01)' },
  { group: 'Oracle',  value: 'bid_oracle_abs',  label: 'Bid → Oracle Sapma >', hint: 'USD (örn: 0.0005)' },
  { group: 'Oracle',  value: 'bid_oracle_pct',  label: 'Bid → Oracle Sapma %', hint: '% (örn: 0.1)' },
  { group: 'Oracle',  value: 'ask_oracle_abs',  label: 'Ask → Oracle Sapma >', hint: 'USD (örn: 0.0005)' },
  { group: 'Oracle',  value: 'ask_oracle_pct',  label: 'Ask → Oracle Sapma %', hint: '% (örn: 0.1)' },
] as const;

function makeId() { return Math.random().toString(36).slice(2, 10); }
function fmtSym(s: string) { return s.replace('PERP_','').replace('_USDC','').replace('-PERP',''); }

export default function AlertModal({ dark = true, symbols, onClose, onSubmit }: Props) {
  const t = getTheme(dark);

  const [symbol,    setSymbol]    = useState(symbols[0] || '');
  const [condition, setCondition] = useState<AlertDraft['condition']>('price_gt');
  const [threshold, setThreshold] = useState('');
  const [note,      setNote]      = useState('');
  const [oneShot,   setOneShot]   = useState(true);

  const hint = CONDITIONS.find(c => c.value === condition)?.hint ?? '';

  const submit = () => {
    const thr = parseFloat(threshold);
    if (!symbol || isNaN(thr)) return;
    onSubmit({
      id: makeId(),
      symbol,
      condition,
      threshold: thr,
      note: note.trim(),
      one_shot: oneShot,
      active: true,
      created_at: Date.now() / 1000,
      triggered_at: null,
    });
    onClose();
  };

  const inp = `border rounded-lg px-3 py-2 text-xs focus:outline-none w-full ${t.inp}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className={`${t.card} border ${t.cardBorder} rounded-2xl w-full max-w-sm mx-4 shadow-2xl`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-5 py-4 border-b ${t.cardBorder}`}>
          <div className="flex items-center gap-2">
            <Bell size={15} className="text-yellow-400" />
            <span className={`font-bold text-sm ${t.text}`}>Alert Oluştur</span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* Sembol */}
          <div>
            <label className={`block text-xs font-medium mb-1.5 ${t.muted}`}>Sembol</label>
            <select value={symbol} onChange={e => setSymbol(e.target.value)} className={inp}>
              {symbols.map(s => (
                <option key={s} value={s}>{fmtSym(s)}</option>
              ))}
            </select>
          </div>

          {/* Koşul */}
          <div>
            <label className={`block text-xs font-medium mb-1.5 ${t.muted}`}>Koşul</label>
            <select value={condition} onChange={e => setCondition(e.target.value as any)} className={inp}>
              {(['Fiyat', 'Funding', 'Oracle'] as const).map(group => (
                <optgroup key={group} label={group}>
                  {CONDITIONS.filter(c => c.group === group).map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {/* Eşik değeri */}
          <div>
            <label className={`block text-xs font-medium mb-1.5 ${t.muted}`}>
              Eşik Değeri
              <span className="ml-1.5 text-blue-400 font-normal">({hint})</span>
            </label>
            <input
              type="number"
              step="any"
              value={threshold}
              onChange={e => setThreshold(e.target.value)}
              placeholder="0.00"
              className={inp}
            />
          </div>

          {/* Not */}
          <div>
            <label className={`block text-xs font-medium mb-1.5 ${t.muted}`}>Not (opsiyonel)</label>
            <input
              type="text"
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Hatırlatıcı not..."
              className={inp}
            />
          </div>

          {/* Tek tetiklensin mi */}
          <label className="flex items-center gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={oneShot}
              onChange={e => setOneShot(e.target.checked)}
              className="accent-yellow-400 w-3.5 h-3.5"
            />
            <span className={`text-xs ${t.muted}`}>Tetiklendikten sonra pasife al</span>
          </label>
        </div>

        {/* Footer */}
        <div className={`flex gap-3 px-5 py-4 border-t ${t.cardBorder}`}>
          <button
            onClick={submit}
            disabled={!threshold || isNaN(parseFloat(threshold))}
            className="flex-1 py-2 rounded-lg text-xs font-bold bg-yellow-400 hover:bg-yellow-300 text-[#061530] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Alert Oluştur
          </button>
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg text-xs font-medium ${t.btnSecondary}`}
          >
            İptal
          </button>
        </div>
      </div>
    </div>
  );
}
